import { EmailSuppressionRow } from "@signalist/shared-types";

import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { TableColumn } from "@/components/Table/Table";

import { formatSuppressedOn, suppressionReasonText, Translate } from "./suppressionDisplay";

const dash = <span className="text-sm text-text-meta">-</span>;

/**
 * The Suppressed tab's columns (PREC-4266, confirmed mockup): Name with the stored suppressed
 * address under it, Company, SUPPRESSED REASON, SUPPRESSED ON and one Remove button per row. The
 * Blacklist-only columns are not part of this table. Rows arrive newest first from the API, so no
 * column sorts.
 */
export function buildSuppressedColumns(
	t: Translate,
	onRemove: (row: EmailSuppressionRow) => void
): TableColumn[] {
	return [
		{
			title: t("contacts.suppressed.nameColumn"),
			uid: "suppressedName",
			minWidth: "220px",
			locked: true,
			hideable: false,
			moveable: false,
			show: true,
			render: (row: EmailSuppressionRow) => (
				<div className="flex flex-col">
					<span className="text-sm font-medium text-text">{row.prospectName || "-"}</span>
					<span className="text-xs text-text-meta" data-testid="suppressed-email">
						{row.email}
					</span>
				</div>
			),
		},
		{
			title: t("contacts.suppressed.companyColumn"),
			uid: "suppressedCompany",
			minWidth: "180px",
			hideable: true,
			moveable: true,
			show: true,
			render: (row: EmailSuppressionRow) =>
				row.organizationName ? (
					<span className="text-sm text-text">{row.organizationName}</span>
				) : (
					dash
				),
		},
		{
			title: t("contacts.suppressed.reasonColumn"),
			uid: "suppressedReason",
			minWidth: "180px",
			hideable: true,
			moveable: true,
			show: true,
			render: (row: EmailSuppressionRow) => (
				<span className="text-sm text-text">{suppressionReasonText(row, t)}</span>
			),
		},
		{
			title: t("contacts.suppressed.suppressedOnColumn"),
			uid: "suppressedOn",
			minWidth: "160px",
			hideable: true,
			moveable: true,
			show: true,
			render: (row: EmailSuppressionRow) => (
				<span className="text-sm text-text">{formatSuppressedOn(row.suppressedAt)}</span>
			),
		},
		{
			title: t("contacts.suppressed.removeColumn"),
			uid: "suppressedRemove",
			width: "fit-content",
			minWidth: "120px",
			hideable: true,
			moveable: true,
			show: true,
			render: (row: EmailSuppressionRow) => (
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="w-3 mr-1" icon={faTrashCan} />}
					size={ButtonSize.XS}
					testId={`suppressed-remove-${row.id}`}
					title={t("contacts.suppressed.removeAction")}
					type="button"
					onClick={(event) => {
						event.stopPropagation();
						onRemove(row);
					}}
				/>
			),
		},
	];
}
